import { type InputHTMLAttributes, type ReactNode, forwardRef, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
  showPasswordToggle?: boolean;
  /** Show a red "*" next to the label. Opt-in and independent of the native `required` attribute. */
  requiredMark?: boolean;
  /** Show a muted "(optional)" tag next to the label. Ignored when `requiredMark` is set. */
  optional?: boolean;
}

/**
 * FieldLabelMark — the required "*" / "(optional)" adornment shared by the form
 * controls. Marked `aria-hidden` so it never leaks into the accessible name.
 * It is opt-in (via `requiredMark`/`optional`), never derived from the native
 * `required` attribute, so existing forms keep their labels unchanged.
 */
export function FieldLabelMark({ requiredMark, optional }: { requiredMark?: boolean; optional?: boolean }) {
  if (requiredMark) {
    return (
      <span aria-hidden="true" className="ml-0.5 text-[var(--danger)]">
        *
      </span>
    );
  }
  if (optional) {
    return (
      <span aria-hidden="true" className="ml-1 font-normal text-[var(--text-3)]">
        (optional)
      </span>
    );
  }
  return null;
}

/**
 * Input — Pure presentational form field.
 * Matches the design-system input style from docs/unbundle-design/Medrecordly.dc.html
 *
 * NO business logic — only styling and event forwarding.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, icon, showPasswordToggle, type, optional, requiredMark, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const resolvedType = showPasswordToggle && type === 'password' && isPasswordVisible ? 'text' : type;

    return (
      <div className="flex flex-col gap-[7px]">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]"
          >
            {label}
            <FieldLabelMark requiredMark={requiredMark} optional={optional} />
          </label>
        )}
        <div className="relative">
          {icon && (
            <span className="pointer-events-none absolute left-[14px] top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-[var(--text-3)]">
              {icon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={cn(
              'w-full h-12 px-[15px]',
              icon && 'pl-[42px]',
              showPasswordToggle && type === 'password' && 'pr-[44px]',
              'border border-transparent rounded-[12px]',
              'bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--text)]',
              'text-[14.5px] placeholder:text-[var(--text-3)]',
              'outline-none transition-[background-color,border-color,box-shadow] duration-200',
              'focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-4 focus:ring-[var(--ring)]',
              error && 'bg-[var(--danger-bg)] border-[var(--danger)] focus:bg-[var(--danger-bg)] focus:border-[var(--danger)] focus:ring-[var(--danger-bg)]',
              className
            )}
            {...props}
          />
          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              onClick={() => setIsPasswordVisible((current) => !current)}
              className="absolute right-[12px] top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[7px] text-[var(--text-3)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            >
              {isPasswordVisible ? (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 102.8 2.8M9.9 4.2A10.4 10.4 0 0112 4c5 0 8.5 4.2 9.5 6-.4.7-1.4 2-2.8 3.2M6.6 6.6C4.6 7.8 3.2 9.4 2.5 11c1 1.8 4.5 6 9.5 6 1.5 0 2.9-.4 4.1-1" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                </svg>
              )}
            </button>
          )}
        </div>
        {error && (
          <span className="flex items-center gap-1 text-[12px] text-[var(--danger)] font-medium animate-[shake_0.3s]">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
            </svg>
            {error}
          </span>
        )}
        {hint && !error && (
          <span className="text-[12px] text-[var(--text-3)]">{hint}</span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/* ─── Textarea variant ─────────────────────────────────────── */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Show a red "*" next to the label. Opt-in and independent of the native `required` attribute. */
  requiredMark?: boolean;
  /** Show a muted "(optional)" tag next to the label. Ignored when `requiredMark` is set. */
  optional?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, optional, requiredMark, ...props }, ref) => {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="flex flex-col gap-[7px]">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]"
          >
            {label}
            <FieldLabelMark requiredMark={requiredMark} optional={optional} />
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            'w-full px-[15px] py-[11px]',
            'border border-transparent rounded-[12px]',
            'bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--text)]',
            'text-[14.5px] placeholder:text-[var(--text-3)]',
            'outline-none transition-[background-color,border-color,box-shadow] duration-200 resize-vertical',
            'focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-4 focus:ring-[var(--ring)]',
            error && 'bg-[var(--danger-bg)] border-[var(--danger)] focus:bg-[var(--danger-bg)] focus:border-[var(--danger)] focus:ring-[var(--danger-bg)]',
            className
          )}
          {...props}
        />
        {error && (
          <span className="text-[12px] text-[var(--danger)] font-medium">{error}</span>
        )}
        {hint && !error && (
          <span className="text-[12px] text-[var(--text-3)]">{hint}</span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

/* ─── Select variant ───────────────────────────────────────── */
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Show a red "*" next to the label. Opt-in and independent of the native `required` attribute. */
  requiredMark?: boolean;
  /** Show a muted "(optional)" tag next to the label. Ignored when `requiredMark` is set. */
  optional?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, hint, id, children, optional, requiredMark, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="flex flex-col gap-[7px]">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]"
          >
            {label}
            <FieldLabelMark requiredMark={requiredMark} optional={optional} />
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'w-full h-12 px-[15px]',
            'border border-transparent rounded-[12px]',
            'bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--text)]',
            'text-[14.5px] cursor-pointer',
            'outline-none transition-[background-color,border-color,box-shadow] duration-200',
            'focus:bg-[var(--surface)] focus:border-[var(--primary)] focus:ring-4 focus:ring-[var(--ring)]',
            error && 'bg-[var(--danger-bg)] border-[var(--danger)] focus:bg-[var(--danger-bg)] focus:border-[var(--danger)] focus:ring-[var(--danger-bg)]',
            className
          )}
          {...props}
        >
          {children}
        </select>
        {error && (
          <span className="text-[12px] text-[var(--danger)] font-medium">{error}</span>
        )}
        {hint && !error && (
          <span className="text-[12px] text-[var(--text-3)]">{hint}</span>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
